import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, FileSpreadsheet, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, KpiCard } from "@/components/KpiCard";
import { DamageDialog } from "@/components/damages/DamageDialog";
import { DriveConnect } from "@/components/damages/DriveConnect";
import { DamageView } from "@/components/damages/DamageView";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtEur, fmtDate, stationColor } from "@/lib/format";
import { useSort, SortTh } from "@/lib/sort";

const StatusPill = ({ s }) => (
  <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s === "CHIUSA" ? "border-slate-200 bg-slate-100 text-slate-600" : "border-amber-200 bg-amber-50 text-amber-700"}`}>{s === "CHIUSA" ? "Chiusa" : "Aperta"}</span>
);

export default function DamagesPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ damages: [], summary: {} });
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => {
    api.get("/damages", { params: { station_id: isAdmin && stationId !== "all" ? stationId : undefined } }).then((r) => setData(r.data));
  }, [isAdmin, stationId]);
  useEffect(load, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles([...r.data.vehicles, ...r.data.incoming]));
  }, []);

  const remove = async (d) => {
    if (!window.confirm(`Eliminare il sinistro di ${d.plate}?`)) return;
    try { await api.delete(`/damages/${d.id}`); toast.success("Sinistro eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = async () => {
    try {
      const { data: blob, headers } = await api.get("/damages/export", { params: { station_id: isAdmin && stationId !== "all" ? stationId : undefined }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || "franchigie.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const { damages: rawDamages, summary } = data;
  const { sorted: damages, sort, toggle } = useSort(rawDamages, { photos: (d) => d.photos.length });
  const S = (p) => <SortTh sort={sort} onSort={toggle} {...p} />;
  const vehicleList = isAdmin && stationId !== "all" ? vehicles.filter((v) => v.station_id === stationId) : vehicles;
  const title = isAdmin ? (stationId === "all" ? "Sinistri e franchigie" : `Sinistri ${stations.find((s) => s.id === stationId)?.name || ""}`) : `Sinistri ${stations[0]?.name || ""}`;

  return (
    <div className="fade-up">
      <PageHeader title={title} subtitle="Danni ai mezzi con driver, descrizione, foto e franchigia.">
        <Button variant="outline" onClick={exportXlsx} data-testid="damage-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button onClick={() => setEditing({})} disabled={!vehicleList.length} data-testid="damage-add-button"><Plus className="mr-2 h-4 w-4" /> Nuovo sinistro</Button>
      </PageHeader>

      {isAdmin && <DriveConnect />}
      {isAdmin && (
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1" data-testid="damage-station-tabs">
          {[{ id: "all", name: "Tutte le stazioni" }, ...stations].map((s) => (
            <button key={s.id} onClick={() => setStationId(s.id)} data-testid={`damage-tab-${s.id}`}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${stationId === s.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
              {s.id !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: stationColor(stations, s.id) }} />}{s.name}
            </button>
          ))}
        </div>
      )}

      <div className={`mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4`}>
        <KpiCard label="Sinistri" value={summary.total ?? 0} testId="damage-kpi-total" />
        <KpiCard label="Pratiche aperte" value={summary.open ?? 0} accent="#D97706" testId="damage-kpi-open" />
        <KpiCard label="Totale franchigie" value={fmtEur(summary.franchise_total)} accent="#E11D48" testId="damage-kpi-franchise" />
        <KpiCard label="Franchigia da inserire" value={summary.franchise_missing ?? 0} accent="#475569" testId="damage-kpi-missing" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="damage-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap">
              <tr><S k="incident_date">Data</S><S k="plate">Targa</S>{isAdmin && <S k="station_name">Stazione</S>}<S k="driver_name">Driver</S>
                <S k="description">Descrizione</S><S k="photos">Foto</S><S k="franchise_amount" className="px-3 py-2 text-right" align="right">Franchigia</S><S k="status">Stato</S><th className="px-3 py-2" /></tr>
            </thead>
            <tbody className="divide-y">
              {damages.map((d) => (
                <tr key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setViewing(d)} data-testid={`damage-row-${d.id}`}>
                  <td className="whitespace-nowrap px-3 py-2 font-medium">{fmtDate(d.incident_date)}</td>
                  <td className="whitespace-nowrap px-3 py-2"><span className="font-mono-num rounded-md border border-slate-300 bg-slate-50 px-2 py-0.5 text-xs font-semibold">{d.plate}</span><span className="ml-2 text-xs text-slate-500">{d.model}</span></td>
                  {isAdmin && <td className="whitespace-nowrap px-3 py-2">{d.station_name}</td>}
                  <td className="whitespace-nowrap px-3 py-2">{d.driver_name}</td>
                  <td className="max-w-sm px-3 py-2 text-slate-600"><p className="line-clamp-2">{d.description}</p></td>
                  <td className="px-3 py-2"><span className="inline-flex items-center gap-1 text-slate-600" data-testid={`damage-photos-count-${d.id}`}><ImageIcon className="h-4 w-4" />{d.photos.length}</span></td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold" data-testid={`damage-franchise-${d.id}`}>{d.franchise_amount == null ? <span className="font-sans text-xs font-normal text-slate-400">da inserire</span> : fmtEur(d.franchise_amount)}</td>
                  <td className="px-3 py-2"><StatusPill s={d.status} /></td>
                  <td className="whitespace-nowrap px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" variant="ghost" onClick={() => setViewing(d)} data-testid={`damage-view-${d.id}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(d)} data-testid={`damage-edit-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    {isAdmin && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(d)} data-testid={`damage-delete-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </td>
                </tr>
              ))}
              {damages.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-slate-500" data-testid="damage-empty">Nessun sinistro registrato.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <DamageView damage={viewing} onClose={() => setViewing(null)} onEdit={(d) => { setViewing(null); setEditing(d); }} />
      <DamageDialog damage={editing} vehicles={vehicleList} isAdmin={isAdmin} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
